import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from "../../firebase"; 
import { collection, addDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../auth/AuthContext'; // Import the useAuth hook

// NOTE: This component now handles both Add and Edit functionality.
function AddCategoryPage() {
  const navigate = useNavigate();
  const { categoryId } = useParams(); // Get the ID from the URL for editing
  const isEditing = Boolean(categoryId);
  const { hasPermission } = useAuth(); // Get the permission check function

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [imageBase64, setImageBase64] = useState(''); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // This useEffect will fetch the category data if we are in "edit" mode
  useEffect(() => {
    if (isEditing) {
      const fetchCategory = async () => {
        setLoading(true);
        try {
          const docRef = doc(db, 'Category meals', categoryId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setName(data.name || '');
            setSlug(data.slug || '');
            setImageBase64(data.imageUrl || '');
          } else {
            setError('Category not found.');
          }
        } catch (err) {
          console.error("Error fetching category:", err);
          setError('Failed to load category data.');
        } finally {
          setLoading(false);
        }
      };
      fetchCategory();
    }
  }, [categoryId, isEditing]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 750 * 1024) { 
      setError('File is too large. Please select an image under 750KB.');
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setImageBase64(reader.result);
    };
    reader.onerror = (err) => {
        console.error("FileReader error: ", err);
        setError("Failed to read the image file.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- PERMISSION CHECK ---
    if (isEditing && !hasPermission('categories.update')) {
      return setError("You do not have permission to update categories.");
    }
    if (!isEditing && !hasPermission('categories.create')) {
      return setError("You do not have permission to create categories.");
    }

    if (!name || !slug) {
      setError('Name and Slug are required.');
      return;
    }
    // Note: Image is not required for updating, in case user just wants to change the name
    if (!isEditing && !imageBase64) {
        setError('An image is required when creating a new category.');
        return;
    }

    setLoading(true);
    setError('');

    const categoryData = {
      name: name,
      slug: slug,
      imageUrl: imageBase64,
      updatedAt: serverTimestamp(),
    };

    try {
      if (isEditing) {
        // UPDATE existing document
        const docRef = doc(db, 'Category meals', categoryId);
        await updateDoc(docRef, categoryData);
        alert('Category updated successfully!');
      } else {
        // CREATE new document
        categoryData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'Category meals'), categoryData);
        alert('Category added successfully!');
      }
      navigate('/categories'); // Navigate to the category list page
      
    } catch (err) {
      console.error("Error saving document: ", err);
      setError('Failed to save category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Title changes based on whether we are editing or adding */}
      <h2>{isEditing ? 'Edit Category' : 'Add New Category'}</h2>
      <form onSubmit={handleSubmit} style={styles.form}>
        {error && <p style={styles.error}>{error}</p>}
        <div style={styles.formGroup}>
          <label htmlFor="name" style={styles.label}>Name</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
            required
          />
        </div>
        <div style={styles.formGroup}>
          <label htmlFor="slug" style={styles.label}>Slug</label>
          <input
            type="text"
            id="slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            style={styles.input}
            required
          />
        </div>
        <div style={styles.formGroup}>
          <label htmlFor="image" style={styles.label}>Image</label>
          <input
            type="file"
            id="image"
            accept="image/png, image/jpeg, image/gif"
            onChange={handleImageChange}
            style={styles.input}
            // Image is only required when creating, not editing
            required={!isEditing} 
          />
        </div>
        {imageBase64 && <img src={imageBase64} alt="Preview" style={styles.previewImage} />}
        
        <button type="submit" style={styles.submitButton} disabled={loading}>
          {loading ? 'Saving...' : (isEditing ? 'Update Category' : 'Add Category')}
        </button>
      </form>
    </div>
  );
}

const styles = {
    // ... your existing styles are fine
};

export default AddCategoryPage;